import {
  EntityLinks,
  Goal,
  GoalFilterDocumentKeys,
  GoalGroupBy,
  goalInboxID,
  GoalReorderSectionUpdate,
  GoalRoadmapSortingKeys,
  goalsArchivedSectionID,
  GoalSortingMode,
  GroupByMode,
  ID,
  LifeArea,
  lifeAreaOtherID,
  rootId,
  UpdateGoal,
  UpdateLifeArea,
  UserSettings,
} from '@griply/types';

import { getSubGoals } from './goals/get-sub-goals';
import { mergeReorderedSubset } from './merge-reordered-subset';

export type BuildGoalOverviewReorderUpdateOptions = {
  updatedSections: GoalReorderSectionUpdate[];
  updatedGoals: UpdateGoal[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  // A migrated sub-goal sits under the goal dropped into Archived only through these.
  links: EntityLinks;
  groupBy: GoalGroupBy;
  /** Premium gate — de-archiving on reorder is only applied when allowed. */
  allowed: boolean;
  currentGoalGroupSorting?: Record<ID, ID[]>;
  // Stored top-level order the "No goal" section merges into.
  currentRoadmapSorting?: ID[];
  // Which goal list reordered; its own sortingMode flips to Custom.
  filterDocumentKey?: GoalFilterDocumentKeys;
};

export type GoalOverviewReorderUpdate = {
  goalUpdates: UpdateGoal[];
  lifeAreaUpdates: UpdateLifeArea[];
  settingsUpdate: Partial<UserSettings>;
};

export const buildGoalOverviewReorderUpdate = ({
  updatedSections,
  updatedGoals,
  goals,
  lifeAreas,
  links,
  groupBy,
  allowed,
  currentGoalGroupSorting,
  currentRoadmapSorting,
  filterDocumentKey = GoalFilterDocumentKeys.GoalsOverview,
}: BuildGoalOverviewReorderUpdateOptions): GoalOverviewReorderUpdate => {
  const isLifeAreaGrouping = groupBy === GroupByMode.LifeArea;
  const isGoalsOverview =
    filterDocumentKey === GoalFilterDocumentKeys.GoalsOverview;

  // The legacy user-level mode only ever mirrored the goals overview.
  const settingsUpdate: Partial<UserSettings> = {
    ...(isGoalsOverview ? { goalSortingMode: GoalSortingMode.Custom } : {}),
    filters: { [filterDocumentKey]: { sortingMode: GoalSortingMode.Custom } },
  };

  const goalsMap = goals.reduce<Record<ID, Goal>>((acc, goal) => {
    acc[goal.id] = goal;
    return acc;
  }, {});

  const lifeAreaMap = lifeAreas.reduce<Record<ID, LifeArea>>(
    (acc, lifeArea) => {
      acc[lifeArea.id] = lifeArea;
      return acc;
    },
    {},
  );

  // Every emitted order holds only the rows the list rendered; the stored one keeps the rest.
  const goalUpdateMap = new Map<ID, UpdateGoal>(
    updatedGoals.map((goal) => [
      goal.id,
      goal.goalSorting
        ? {
            ...goal,
            goalSorting: mergeReorderedSubset(
              goalsMap[goal.id]?.goalSorting ?? [],
              goal.goalSorting,
            ),
          }
        : { ...goal },
    ]),
  );
  const mergeGoalUpdate = (update: UpdateGoal) => {
    goalUpdateMap.set(update.id, {
      ...goalUpdateMap.get(update.id),
      ...update,
    });
  };

  const lifeAreaUpdates: UpdateLifeArea[] = [];

  const isArchivedSectionUpdated = updatedSections.some(
    ({ id }) => id === goalsArchivedSectionID,
  );

  const archiveGoal = (goalId: ID, archivedAt: Date | null) => {
    const existing = goalUpdateMap.get(goalId);
    if (existing) {
      existing.archivedAt = archivedAt;
      return;
    }
    mergeGoalUpdate({ id: goalId, archivedAt });
  };

  updatedSections.forEach(({ id, goalSorting }) => {
    if (id !== goalsArchivedSectionID && isArchivedSectionUpdated) {
      goalSorting.forEach((goalId) => {
        const goal = goalsMap[goalId];
        if (!goal || !goal.archivedAt || !allowed) {
          return;
        }
        archiveGoal(goalId, null);
      });
    }

    if (id === rootId) {
      if (isLifeAreaGrouping) {
        settingsUpdate.lifeAreaSorting = goalSorting;
        return;
      }
      settingsUpdate.goalRoadmapSorting = {
        [GoalRoadmapSortingKeys.InProgress]: goalSorting,
      };
      return;
    }

    if (id === goalsArchivedSectionID) {
      goalSorting
        .filter((goalId) => goalsMap[goalId])
        .forEach((goalId) => {
          const goal = goalsMap[goalId];
          if (!goal) {
            return;
          }
          if (!goal.archivedAt) {
            archiveGoal(goalId, new Date());
          }

          getSubGoals(goalId, { goals, recursive: true, links }).forEach(
            (subGoal) => {
              if (!subGoal.archivedAt) {
                archiveGoal(subGoal.id, new Date());
              }
            },
          );
        });
      return;
    }

    if (id === goalInboxID) {
      settingsUpdate.goalRoadmapSorting = {
        [GoalRoadmapSortingKeys.InProgress]: mergeReorderedSubset(
          currentRoadmapSorting ?? [],
          goalSorting,
        ),
      };
      return;
    }

    // A goal section (projects grouped by goal, a sub-goal folder) owns that goal's goalSorting.
    const sectionGoal = goalsMap[id];
    if (sectionGoal) {
      mergeGoalUpdate({
        id,
        goalSorting: mergeReorderedSubset(
          sectionGoal.goalSorting ?? [],
          goalSorting,
        ),
      });
      return;
    }

    const lifeArea = lifeAreaMap[id];
    if (lifeArea) {
      lifeAreaUpdates.push({
        id,
        goalSorting: mergeReorderedSubset(
          lifeArea.goalSorting ?? [],
          goalSorting,
        ),
      });
      return;
    }

    const groupKey = id === lifeAreaOtherID ? 'null' : id;
    settingsUpdate.goalGroupSorting = {
      lifeArea: {
        sorting: {
          ...currentGoalGroupSorting,
          ...settingsUpdate.goalGroupSorting?.lifeArea?.sorting,
          [groupKey]: mergeReorderedSubset(
            currentGoalGroupSorting?.[groupKey] ?? [],
            goalSorting,
          ),
        },
      },
    };
  });

  const goalUpdates = Array.from(goalUpdateMap.values()).map((goal) => ({
    ...goal,
    goalSortingMode: GoalSortingMode.Custom,
  }));

  return { goalUpdates, lifeAreaUpdates, settingsUpdate };
};
